import { Button, Text } from '@/ui-kit'
import { useNavigate } from 'react-router-dom'
import { useSubmitMissionResult } from '../api'
import type { Mission } from '../api/models'
import { useMissionsUrl } from '../context/MissionsUrlProvider'
import FileUploader from './FileUploader'
import { MissionDemographicsForm } from './MissionDemographicsForm'
import { MissionExternalActionForm } from './MissionExternalActionForm'
import { MissionNotAvailable } from './MissionNotAvailable'

type MissionContentProps = {
  mission: Mission
}

export const MissionContent = ({ mission }: MissionContentProps) => {
  const { missionsUrl } = useMissionsUrl()

  const { mutate: submitMissionResult } = useSubmitMissionResult()
  const navigate = useNavigate()

  const onUploadSuccess = () => {
    navigate(missionsUrl)
  }

  const onCheckAuthorization = () => {
    if (mission) {
      submitMissionResult({ missionId: mission.id })
    }
  }

  if (!mission.availability.available) {
    return <MissionNotAvailable reason={mission.availability.reason ?? 'unavailable'} />
  }

  return (
    <div className="flex max-w-[845px] flex-col gap-6 py-6">
      {mission.description.sections?.map((section, index) => (
        <div key={`${mission.id}-section-${index}-${section.name}`} className="flex flex-col gap-2">
          <Text type="subtitle-semibold" className="text-gray-70">
            {section.name}
          </Text>
          <div
            dangerouslySetInnerHTML={{ __html: section.body }}
            className="quill-editor text-gray-80 [&_hover:a]:text-orange [&_li]:my-1 [&_ol]:my-2 [&_ol]:ml-6 [&_ol]:list-decimal [&_ul]:my-2 [&_ul]:ml-8 [&_ul]:list-disc"
          />
          {section.image && <img src={section.image} alt={section.name} className="mt-2 rounded-lg" />}
        </div>
      ))}

      {mission.view.latestSubmissionStatus === 'SUBMITTED' ? (
        <Button isDisabled={true} className="max-w-[300px] max-mobile:w-full max-mobile:max-w-full">
          Validating...
        </Button>
      ) : (
        <>
          {mission.type === 'FILE_UPLOAD' && mission.view.nextSubmissionAvailable && (
            <FileUploader
              missionId={mission.id}
              buttonText={mission.view.buttonText}
              onUploadSuccess={onUploadSuccess}
            />
          )}
          {mission.type === 'WEBSITE_LOGIN' && mission.view.nextSubmissionAvailable && (
            <Button onClick={onCheckAuthorization} className="max-w-[300px] max-mobile:w-full max-mobile:max-w-full">
              Check authorization
            </Button>
          )}
          {mission.type === 'EXTERNAL_ACTION' && mission.view.nextSubmissionAvailable && (
            <MissionExternalActionForm mission={mission} />
          )}
        </>
      )}

      {mission.type === 'PROFILE' && <MissionDemographicsForm mission={mission} />}
    </div>
  )
}
